import { useRef } from 'react';
import constate from 'constate';
import ReactPlayer from 'react-player/file';
const SRC = 'storyboard.provider.ts';

import { NUMBER_OF_PLAYERS } from 'constants/params';
import { useAppDispatch } from 'store/store-hooks';
import { getInternalPlayer } from 'components/app/_helpers/_app.helper';
import { setIsIdle, setReadyPlayersListener } from 'components/app/storyboard/_mediator/sb.slice';

export type TrackRoot = HTMLDivElement;
export type TrackFeed = HTMLDivElement;
export type ProgressPayload = { played: Seconds; loaded: Seconds; duration: Seconds };

export enum onInitEnum {
  'onSeek',
  'onSelect',
  'onCellResize',
}

const useSbPlayerProvider = () => {
  const playerRootElement = useRef<HTMLDivElement>(null);
  const activePlayerIndex = useRef<Index>(0);
  const faderBg = useRef<HTMLDivElement>(null);
  const faderText = useRef<HTMLDivElement>(null);
  const sbPlayers = useRef<(ReactPlayer | null)[]>(new Array(NUMBER_OF_PLAYERS).fill(null));
  const readyToPlay = useRef<boolean[]>(Array.from({ length: NUMBER_OF_PLAYERS }, () => false));
  const onInitParam = useRef<onInitEnum>(onInitEnum.onSeek);
  const idleTimeoutID = useRef<NodeJS.Timeout>();
  const idleTime = useRef<Seconds>(0);
  const dispatch = useAppDispatch();

  const sbPlayerGetPayload = (index: Index): ProgressPayload | undefined => {
    const player = sbPlayers.current[index];
    if (!player) {
      console.error(`[${SRC}] sbPlayerGetPayload: player is not available`);
      return;
    }
    const duration = player.getDuration();
    const played = player.getCurrentTime();
    const loaded = player.getSecondsLoaded() - played;
    return { played, loaded, duration };
  };

  const sbPlayerPause = () => {
    sbPlayers.current.forEach((ref) => {
      const player = getInternalPlayer(ref);
      if (player) player.pause();
    });
  };

  const sbPlayerPlay = (players: Index[]) => {
    sbPlayers.current.forEach((ref, i) => {
      const player = getInternalPlayer(ref);
      if (players.includes(i)) {
        if (!player) {
          console.error(`[${SRC}] play: player doesn't exist`);
          return;
        }
        if (!player.ended) player.play();
      } else if (player) player.pause();
    });
  };

  const sbPlayerSeekTo = (player: Index, time: Seconds) => {
    const el = getInternalPlayer(sbPlayers.current[player]);
    if (!el) return;
    el.currentTime = time;
    readyToPlay.current[player] = false;
  };

  const sbPlayerReady = (player: Index) => {
    readyToPlay.current[player] = true;
    dispatch(setReadyPlayersListener());
  };

  const sbSetIdle = (idle: Seconds) => {
    idleTime.current = idle;
    dispatch(setIsIdle(!!idle));
  };

  return {
    playerRootElement,
    sbPlayerGetPayload,
    activePlayerIndex,
    sbPlayerReady,
    onInitParam,
    sbSetIdle,
    faderBg,
    idleTime,
    faderText,
    sbPlayers,
    readyToPlay,
    sbPlayerPlay,
    sbPlayerPause,
    idleTimeoutID,
    sbPlayerSeekTo,
  };
};

export const [SbPlayerProvider, useSbPlayerContext] = constate(useSbPlayerProvider);
